/**
 * Phase 1 User Rate persistence helpers.
 *
 * Eventual SoT: organization_members.hourly_rate (field on the member record —
 * not a catalog or matrix row).
 * Temporary: dual-write users.hourly_rate for backwards compatibility.
 */

/**
 * @returns {{ found: boolean, hourlyRate: number|null, error: object|null }}
 */
export async function fetchOrganizationMemberUserRate(
  {
    supabaseUserId,
    organizationId = null,
  },
  client,
) {
  if (!client || !supabaseUserId) {
    return { found: false, hourlyRate: null, error: null };
  }

  // A4 (t124u): hourly_rate is not column-readable on organization_members;
  // cp_member_hourly_rate returns it only to the member, Owner/Admin,
  // payroll_access holders, or the member's manager (cost snapshot).
  const { data, error } = await client.rpc("cp_member_hourly_rate", {
    p_supabase_user_id: supabaseUserId,
    p_organization_id: organizationId,
  });

  if (error) {
    return { found: false, hourlyRate: null, error };
  }

  if (data == null || data === "") {
    return { found: false, hourlyRate: null, error: null };
  }

  return {
    found: true,
    hourlyRate: Number(data),
    error: null,
  };
}

/**
 * Dual-write User Rate to organization_members (preferred) and users (compat).
 * Call sites: Admin Payroll Rates User Rate editor, Manager employee tools.
 *
 * Pass hourlyRate "" or null to clear both stores (manager tools).
 * Non-empty values must be a positive number.
 */
export async function dualWriteUserRate(
  {
    supabaseUserId,
    hourlyRate,
    organizationId = null,
  },
  client,
) {
  if (!client) {
    throw new Error("Database client required.");
  }

  if (!supabaseUserId) {
    throw new Error("Select an employee.");
  }

  const rate =
    hourlyRate === "" || hourlyRate == null ? null : Number(hourlyRate);

  if (rate != null && !Number.isFinite(rate)) {
    throw new Error("Enter a valid hourly rate.");
  }

  if (rate != null && rate <= 0) {
    throw new Error("Enter a valid hourly rate.");
  }

  const { error: userError } = await client
    .from("users")
    .update({ hourly_rate: rate })
    .eq("supabase_user_id", supabaseUserId);

  if (userError) {
    throw userError;
  }

  const { data: profile, error: profileError } = await client
    .from("profiles")
    .select("id")
    .eq("supabase_user_id", supabaseUserId)
    .maybeSingle();

  if (profileError) {
    throw profileError;
  }

  if (!profile?.id) {
    return { usersUpdated: true, membershipUpdated: false };
  }

  let membershipQuery = client
    .from("organization_members")
    .update({ hourly_rate: rate })
    .eq("profile_id", profile.id);

  if (organizationId) {
    membershipQuery = membershipQuery.eq("organization_id", organizationId);
  }

  const { error: membershipError } = await membershipQuery;

  if (membershipError) {
    throw membershipError;
  }

  return { usersUpdated: true, membershipUpdated: true };
}
